import api from '../services/api';
import type { MenuItem } from '../components/Layout/Sidebar';

export interface SidebarCustomSection {
  id: string;
  name: string;
  itemKeys: string[];
}

export interface SidebarLayout {
  version: 1;
  /** Ordered list of section keys (default `section:Name` or custom ids). */
  sectionOrder: string[];
  customSections: SidebarCustomSection[];
  /**
   * Module keys shown at root (no group header), after Dashboard / Notifications / Bookmarks —
   * same top-level style as Dashboard.
   */
  topLevelItemKeys?: string[];
}

/** Reserved editor / layout key for ungrouped top-level modules. */
export const SIDEBAR_TOP_LEVEL_KEY = 'top-level';

const PINNED_NAMES = new Set(['Dashboard', 'Notifications', 'Bookmarks']);
const CACHE_KEY = 'atlas_sidebar_layout_cache';

export function isPinnedSidebarItem(item: MenuItem): boolean {
  if (item.sectionLabel) return true;
  return PINNED_NAMES.has(item.name);
}

export function getSectionKey(item: MenuItem): string {
  return `section:${item.name}`;
}

export function getItemKey(item: MenuItem): string {
  const path = item.path ?? item.views?.[0]?.path;
  if (path) return `item:${path}`;
  return `item:name:${item.name}`;
}

export function isEFormMenuItem(item: MenuItem | string): boolean {
  const path = typeof item === 'string' ? item.replace(/^item:/, '') : (item.path ?? item.views?.[0]?.path ?? '');
  return /^\/e-forms\/\d+\//.test(path);
}

/** Flatten navigable leaf items (modules) from a menu tree, including e-form shortcuts. */
export function flattenSidebarLeaves(items: MenuItem[]): MenuItem[] {
  const out: MenuItem[] = [];
  const walk = (list: MenuItem[]) => {
    for (const item of list) {
      if (isPinnedSidebarItem(item) || item.isBookmark) continue;
      const hasNested = !!(item.children && item.children.length > 0);
      if (hasNested) {
        walk(item.children!);
      } else if (item.path || (item.views && item.views.length > 0)) {
        out.push(item);
      }
    }
  };
  walk(items);
  return out;
}

function cloneItem(item: MenuItem): MenuItem {
  return {
    ...item,
    children: item.children?.map(cloneItem),
    views: item.views ? [...item.views] : undefined,
  };
}

/**
 * Apply a user sidebar layout on top of the permission-filtered menu.
 * Unknown keys are ignored. Unassigned modules stay in their original sections.
 * E-form shortcuts remain movable leaves like any other module.
 */
export function applySidebarLayout(items: MenuItem[], layout: SidebarLayout | null | undefined): MenuItem[] {
  if (!layout || layout.version !== 1) return items;

  const structural = items.filter((i) => !isPinnedSidebarItem(i));

  const leafByKey = new Map<string, MenuItem>();
  for (const leaf of flattenSidebarLeaves(structural)) {
    leafByKey.set(getItemKey(leaf), cloneItem(leaf));
  }

  const assigned = new Set<string>();
  for (const key of layout.topLevelItemKeys || []) assigned.add(key);
  for (const section of layout.customSections || []) {
    for (const key of section.itemKeys || []) assigned.add(key);
  }

  const stripAssigned = (item: MenuItem): MenuItem | null => {
    if (item.children && item.children.length > 0) {
      const kids = item.children.map(stripAssigned).filter((c): c is MenuItem => c != null);
      if (kids.length === 0 && !item.path && !(item.views && item.views.length)) return null;
      return { ...cloneItem(item), children: kids };
    }
    if (assigned.has(getItemKey(item))) return null;
    return cloneItem(item);
  };

  const defaultSections = structural
    .map(stripAssigned)
    .filter(
      (s): s is MenuItem =>
        !!s &&
        (!!(s.children && s.children.length > 0) || !!s.path || !!(s.views && s.views.length)),
    );

  const defaultByKey = new Map(defaultSections.map((s) => [getSectionKey(s), s]));

  const customMap = new Map<string, MenuItem>();
  for (const sec of layout.customSections || []) {
    const children = (sec.itemKeys || [])
      .map((k) => leafByKey.get(k))
      .filter((c): c is MenuItem => !!c)
      .map(cloneItem);
    if (children.length === 0) continue;
    customMap.set(sec.id, {
      name: sec.name,
      defaultExpanded: false,
      children,
    });
  }

  const order =
    layout.sectionOrder?.length > 0
      ? layout.sectionOrder
      : [...defaultSections.map(getSectionKey), ...Array.from(customMap.keys())];

  const used = new Set<string>();
  const ordered: MenuItem[] = [];

  for (const key of order) {
    if (used.has(key)) continue;
    const fromDefault = defaultByKey.get(key);
    if (fromDefault) {
      ordered.push(fromDefault);
      used.add(key);
      continue;
    }
    const fromCustom = customMap.get(key);
    if (fromCustom) {
      ordered.push(fromCustom);
      used.add(key);
    }
  }

  for (const section of Array.from(defaultByKey.values())) {
    const key = getSectionKey(section);
    if (!used.has(key)) ordered.push(section);
  }
  for (const [key, section] of Array.from(customMap.entries())) {
    if (!used.has(key)) ordered.push(section);
  }

  const topLevel = (layout.topLevelItemKeys || [])
    .map((k) => leafByKey.get(k))
    .filter((c): c is MenuItem => !!c)
    .map(cloneItem);

  const pinnedItems = items.filter((i) => PINNED_NAMES.has(i.name) || i.sectionLabel === 'General').map(cloneItem);
  return [...pinnedItems, ...topLevel, ...ordered];
}

export function getCachedSidebarLayout(): SidebarLayout | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.version === 1 ? (parsed as SidebarLayout) : null;
  } catch {
    return null;
  }
}

export function setCachedSidebarLayout(layout: SidebarLayout | null): void {
  try {
    if (!layout) localStorage.removeItem(CACHE_KEY);
    else localStorage.setItem(CACHE_KEY, JSON.stringify(layout));
  } catch {
    // ignore
  }
}

export function clearSidebarLayoutCache(): void {
  setCachedSidebarLayout(null);
}

export async function fetchSidebarLayout(userId?: number): Promise<SidebarLayout | null> {
  const token = localStorage.getItem('access_token');
  if (!token) {
    setCachedSidebarLayout(null);
    return null;
  }
  try {
    const url = userId != null ? `/users/${userId}/sidebar-layout` : '/profile/sidebar-layout';
    const res = await api.get<{ data: SidebarLayout | null }>(url);
    const layout = res.data?.data ?? null;
    const valid = layout && layout.version === 1 ? layout : null;
    if (userId == null) setCachedSidebarLayout(valid);
    return valid;
  } catch (e) {
    console.error('Failed to fetch sidebar layout', e);
    return userId == null ? getCachedSidebarLayout() : null;
  }
}

export async function saveSidebarLayout(
  layout: SidebarLayout | null,
  options?: { userId?: number; applyLocally?: boolean },
): Promise<SidebarLayout | null> {
  const url = options?.userId != null ? `/users/${options.userId}/sidebar-layout` : '/profile/sidebar-layout';
  const res = await api.put<{ data: SidebarLayout | null }>(url, { layout });
  const saved = res.data?.data ?? null;
  const applyLocally = options?.applyLocally ?? options?.userId == null;
  if (applyLocally) {
    setCachedSidebarLayout(saved);
    window.dispatchEvent(new Event('sidebarLayoutUpdated'));
  }
  return saved;
}

export function createEmptyLayout(fromItems: MenuItem[]): SidebarLayout {
  const structural = fromItems.filter((i) => !isPinnedSidebarItem(i));
  return {
    version: 1,
    sectionOrder: structural.map(getSectionKey),
    customSections: [],
    topLevelItemKeys: [],
  };
}

export function newCustomSectionId(): string {
  return `custom:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}
